import {Injectable} from '@angular/core';
import {BehaviorSubject, ReplaySubject, Subject} from 'rxjs';
import {CellSelection} from '@blocks/table/cell-selection';
import {instances} from '@blocks/rich-text/variable.blot';
import {BlockMutationService} from '@blocks/services/block-mutation/block-mutation.service';
import {Point} from '@angular/cdk/drag-drop';
import {BlockType, RichText, Table, TableCell, TableRow} from '@paperlessio/sdk/api/models';

@Injectable({
  providedIn: 'root'
})
export class TableService {
  currentSelection$ = new ReplaySubject<CellSelection>(1);
  selectedTableBlock$ = new BehaviorSubject<Table>(null);
  keyDown$ = new Subject<KeyboardEvent>();
  keyUp$ = new Subject<KeyboardEvent>();
  mouseUp$ = new Subject<MouseEvent>();
  mouseMove$ = new Subject<MouseEvent>();

  constructor(private blockMutationService: BlockMutationService) { }

  calculateFilledCellBoundary(): Point {
    if (!this.selectedTableBlock$.value) {
      return;
    }

    const emptyCells = this.selectedTableBlock$.value.children.map(row => {
      return row.children.map(cell => {
        const rte = cell.children[0] as RichText;
        const quill = Array.from(instances.entries()).find(kv => kv[1].block.id === rte.id);
        const text = quill ? quill[0]?.getText()?.trim(): '';
        return text?.length <= 0;
      });
    });

    let lastEmptyColIndex = 0;
    let lastEmptyRowIndex = 0;

    for (let y = 0; y < emptyCells.length; y++) {
      for (let x = 0; x < emptyCells[y].length; x++) {
        if (!emptyCells[y][x]) {
          lastEmptyColIndex = x;
          lastEmptyRowIndex = y;
        }
      }
    }

    return {x: lastEmptyColIndex, y: lastEmptyRowIndex};
  }

  getColumnCount(): number {
    return (this.selectedTableBlock$?.value?.children as any).reduce((accumulator, row) => {
      return accumulator <= row.children.length ? row.children.length : accumulator;
    }, 0);
  }

  addColAtIndex(tableBlock: Table, index: number) {
    let pixelWidths = tableBlock.settings.colWidths.map(w => 1000 / 100 * w);
    const newColWidth = 1000 * 0.20;

    // Add new col width at the index of the column instead of the end
    if (index <= 0) {
      pixelWidths.unshift(newColWidth);
    } else {
      const firstPart = pixelWidths.slice(0, index);
      const lastPart = pixelWidths.slice(index, pixelWidths.length);
      pixelWidths = [...firstPart, newColWidth, ...lastPart];
    }

    tableBlock.settings.colWidths = pixelWidths.map(w => w / 1000 * 100);
    this.blockMutationService.addBlockUpdate(tableBlock);

    this.selectedTableBlock$?.value?.children.forEach(row => {
      const newCell = new TableCell({type: BlockType.TableCell});
      this.blockMutationService.addBlockCreate(newCell, row, index ?? row.children.length);

      const newRtb = new RichText({type: BlockType.RichText});
      this.blockMutationService.addBlockCreate(newRtb, newCell, 0);
    });
  }

  addRowAtIndex(index: number) {
    const newRow = new TableRow({type: BlockType.TableRow});
    const columnCount = this.getColumnCount();

    this.blockMutationService.addBlockCreate(
      newRow,
      this.selectedTableBlock$?.value,
      index ?? this.selectedTableBlock$.value.children.length);

    for (let i = 0; i < columnCount; i++) {
      const newCell = new TableCell({type: BlockType.TableCell});
      this.blockMutationService.addBlockCreate(newCell, newRow, i);

      const newRtb = new RichText({type: BlockType.RichText});
      this.blockMutationService.addBlockCreate(newRtb, newCell, 0);
    }
  }

  removeColAtIndex(tableBlock: Table, index: number) {
    this.selectedTableBlock$?.value?.children.forEach(row => {
      const cellToDelete = row.children[index];

      tableBlock.settings.colWidths.splice(index, 1);
      const sum = tableBlock.settings.colWidths.reduce((p, c, i) => p + c, 0);
      tableBlock.settings.colWidths = tableBlock.settings.colWidths.map(w => w / sum * 100);
      this.blockMutationService.addBlockUpdate(tableBlock);

      if (cellToDelete != null) {
        this.blockMutationService.addBlockDelete(cellToDelete);
      }
    });
  }

  removeRowAtIndex(index: number) {
    const foundRow = this.selectedTableBlock$?.value?.children[index];

    if (foundRow == null) {
      return;
    }

    this.blockMutationService.addBlockDelete(foundRow);
  }
}
